<script setup lang="ts">
    import PauseStartImage from '~/assets/icons/play.svg';
    import PauseStopImage from '~/assets/icons/pause.svg';
    import StoryblokImage from '~/storyblok/Image/utils/StoryblokImage.vue';

    const {
        src = '',
        media = null,
        alt = 'Hero Media',
        contentType = '',
        storageKey = 'hero-video-paused',
        clipPathId = 'circle-clip',
        variant = 'circle'
    } = defineProps<{
        src?: string;
        media?: StoryblokAsset | null;
        alt?: string;
        contentType?: string;
        storageKey?: string;
        clipPathId?: string;
        variant?: 'circle' | 'leaf' | 'urology' | 'nephrology';
    }>();

    const { t } = useI18n();
    const videoRef = useTemplateRef<HTMLVideoElement>('videoRef');
    const isVideoPaused = ref(false);

    const contentTypeByExtension: Record<string, string> = {
        mp4: 'video/mp4',
        m4v: 'video/mp4',
        webm: 'video/webm',
        ogg: 'video/ogg',
        ogv: 'video/ogg',

        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        gif: 'image/gif',
        webp: 'image/webp',
        avif: 'image/avif',
        svg: 'image/svg+xml'
    };

    const getContentTypeFromSrc = (src: string): string => {
        const srcWithoutQuery = src.split(/[?#]/)[0]?.toLowerCase() ?? '';
        const extension = srcWithoutQuery.match(/\.([a-z0-9]+)$/)?.[1] ?? '';

        return contentTypeByExtension[extension] ?? '';
    };

    const normalizedContentType = computed(() => {
        const uploadedContentType = contentType.trim().toLowerCase();
        const inferredContentType = getContentTypeFromSrc(src);

        if (uploadedContentType.startsWith('video/') || uploadedContentType.startsWith('image/')) {
            if (uploadedContentType.endsWith('/*')) {
                return inferredContentType || uploadedContentType;
            }

            return uploadedContentType;
        }

        return inferredContentType || uploadedContentType;
    });

    const isVideo = computed(() => normalizedContentType.value.startsWith('video/'));
    const isImage = computed(() => normalizedContentType.value.startsWith('image/'));

    const videoSourceType = computed(() => {
        if (!isVideo.value || normalizedContentType.value.endsWith('/*')) {
            return undefined;
        }

        return normalizedContentType.value;
    });

    const toggleVideo = () => {
        if (!isVideo.value || !videoRef.value) {
            return;
        }

        const shouldPlay = sessionStorage.getItem(storageKey) === 'true';

        if (shouldPlay) {
            videoRef.value
                .play()
                .then(() => {
                    sessionStorage.setItem(storageKey, 'false');
                    isVideoPaused.value = false;
                })
                .catch(() => {
                    sessionStorage.setItem(storageKey, 'true');
                    isVideoPaused.value = true;
                });

            return;
        }

        videoRef.value.pause();
        sessionStorage.setItem(storageKey, 'true');
        isVideoPaused.value = true;
    };

    onMounted(() => {
        if (!isVideo.value || !videoRef.value) {
            return;
        }

        if (sessionStorage.getItem(storageKey) === 'true') {
            videoRef.value.pause();
            isVideoPaused.value = true;
            return;
        }

        videoRef.value.play().catch(() => {
            isVideoPaused.value = true;
            sessionStorage.setItem(storageKey, 'true');
        });
    });
</script>

<template>
    <div
        class="hero-media"
        :class="`hero-media--${variant}`"
    >
        <div class="hero-media__wrapper">
            <slot name="clipPath">
                <svg
                    aria-hidden="true"
                    viewBox="0 0 1 1"
                    style="fill-rule: evenodd; clip-rule: evenodd; stroke-linejoin: round; stroke-miterlimit: 2"
                >
                    <defs>
                        <clipPath
                            :id="clipPathId"
                            clipPathUnits="objectBoundingBox"
                        >
                            <circle
                                cx="0.5"
                                cy="0.5"
                                r="0.5"
                            />
                        </clipPath>
                    </defs>
                </svg>
            </slot>

            <video
                v-if="isVideo && src"
                ref="videoRef"
                autoplay
                muted
                loop
                playsinline
            >
                <source
                    :src="src"
                    :type="videoSourceType"
                />
            </video>

            <StoryblokImage
                v-else-if="(variant === 'urology' || variant === 'nephrology') && media?.filename"
                :media="media"
                type="banner"
                :alt="alt"
                eager
            />

            <img
                v-else-if="isImage && src"
                :src="src"
                :alt="alt"
                loading="eager"
                decoding="async"
            />

            <div
                v-else
                class="media-fallback"
                aria-hidden="true"
            />
        </div>
        <div class="hero-media__toggle-wrapper">
            <button
                v-if="isVideo"
                class="hero-media__toggle"
                type="button"
                @click="toggleVideo"
            >
                <div>
                    <img
                        :src="isVideoPaused ? PauseStartImage : PauseStopImage"
                        :alt="isVideoPaused ? t('general.hero.altVideoStart') : t('general.hero.altVideoPause')"
                        :class="{ paused: isVideoPaused }"
                    />
                </div>
            </button>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/hero-section/hero-media.scss';
</style>
